/**
 * Phase 33 — the days of the week a container runs, and the weekend share
 * of its amounts. The weekday is the RECIPIENT-LOCAL weekday of the run
 * date (a YYYY-MM-DD the loader already derived in the country's timezone),
 * so no timezone math happens here. A container whose days do not include
 * today is skipped like a switched-off one; Saturday and Sunday may send a
 * share of each amount, before capacity and the caps.
 * For Media Devoted | Created By Ciro Autiero
 */

export const DROP_GEN_WEEKDAYS = [
  "MO",
  "TU",
  "WE",
  "TH",
  "FR",
  "SA",
  "SU",
] as const;
export type DropGenWeekday = (typeof DROP_GEN_WEEKDAYS)[number];

export const DROP_GEN_WEEKDAY_LABELS: Record<DropGenWeekday, string> = {
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
  SU: "Sun",
};

export const DROP_GEN_WEEKDAY_NAMES: Record<DropGenWeekday, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};

const WEEKDAYS: readonly DropGenWeekday[] = ["MO", "TU", "WE", "TH", "FR"];
const WEEKEND: readonly DropGenWeekday[] = ["SA", "SU"];

/** The weekday of a YYYY-MM-DD date — already local, read as a plain date. */
export function weekdayOf(isoDate: string): DropGenWeekday {
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
  const sundayFirst = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return DROP_GEN_WEEKDAYS[(sundayFirst + 6) % 7];
}

export const isWeekend = (day: DropGenWeekday): boolean =>
  day === "SA" || day === "SU";

/** Tolerant read of a stored list: unknown codes dropped, week order kept;
 *  anything that is not a list (an old payload) = every day. */
export function normalizeRunDays(raw: unknown): DropGenWeekday[] {
  if (!Array.isArray(raw)) return [...DROP_GEN_WEEKDAYS];
  const picked = new Set(raw.filter(isWeekday));
  return DROP_GEN_WEEKDAYS.filter((d) => picked.has(d));
}

const isWeekday = (v: unknown): v is DropGenWeekday =>
  typeof v === "string" &&
  (DROP_GEN_WEEKDAYS as readonly string[]).includes(v);

/** Does a container with these days run on that date? */
export function runsOn(
  runDays: readonly string[] | null | undefined,
  isoDate: string,
): boolean {
  return normalizeRunDays(runDays ?? undefined).includes(weekdayOf(isoDate));
}

/** "every day" · "weekdays" · "weekends" · "Fri–Sat" · "Mon, Wed, Fri" · "no day". */
export function describeRunDays(
  raw: readonly string[] | null | undefined,
): string {
  const days = normalizeRunDays(raw ?? undefined);
  if (days.length === 7) return "every day";
  if (days.length === 0) return "no day";
  if (sameSet(days, WEEKDAYS)) return "weekdays";
  if (sameSet(days, WEEKEND)) return "weekends";
  const labels = days.map((d) => DROP_GEN_WEEKDAY_LABELS[d]);
  if (days.length >= 2 && contiguous(days))
    return `${labels[0]}–${labels[labels.length - 1]}`;
  return labels.join(", ");
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && b.every((d) => a.includes(d));

const contiguous = (days: readonly DropGenWeekday[]) => {
  const first = DROP_GEN_WEEKDAYS.indexOf(days[0]);
  return days.every((d, i) => DROP_GEN_WEEKDAYS[first + i] === d);
};

/** A weekend day sends `pct` % of the amount (never under 1 SMS); 100 = unchanged. */
export function weekendAmount(
  amount: number,
  pct: number,
  day: DropGenWeekday,
): number {
  if (!isWeekend(day) || pct >= 100) return amount;
  return Math.max(1, Math.round((amount * pct) / 100));
}

/** "weekends 60 %" when a weekend day is on and the share is under 100; else null. */
export function describeWeekendShare(
  raw: readonly string[] | null | undefined,
  pct: number | null | undefined,
): string | null {
  const share = pct ?? 100;
  if (share >= 100) return null;
  const days = normalizeRunDays(raw ?? undefined);
  return days.some(isWeekend) ? `weekends ${share} %` : null;
}
